import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { PlaylistFilters, PlaylistResult } from "../schemas/playlistSchema";
import { playlistService } from "../api/services/playlistService";

export const usePlaylists = (filters: PlaylistFilters = {}) => {
  const queryClient = useQueryClient();

  const {
    data: playlists,
    isLoading: isPlaylistsLoading,
    refetch,
  } = useQuery<PlaylistResult, Error>({
    queryKey: ["playlists", filters],
    queryFn: () => playlistService.getAll(filters),
  });

  const createPlaylistMutation = useMutation({
    mutationFn: playlistService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlists"] });
      toast.success("Playlist created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create playlist");
    },
  });

  const updatePlaylistMutation = useMutation({
    mutationFn: playlistService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlists"] });
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      queryClient.refetchQueries({ queryKey: ["playlist-assignments"] });
      toast.success("Playlist updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update playlist");
    },
  });

  const deletePlaylistMutation = useMutation({
    mutationFn: playlistService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["playlists"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Playlist deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete playlist");
    },
  });

  const clonePlaylistMutation = useMutation({
    mutationFn: playlistService.clone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlists"] });
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      toast.success("Playlist cloned successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to clone playlist");
    },
  });

  const fetchPlaylistItems = async (playlistId: string) => {
    try {
      return await playlistService.getPlaylistItems(playlistId);
    } catch (error) {
      toast.error("Failed to load playlist items");
      throw error;
    }
  };

  const deletePlaylistItem = async (playlistId: string) => {
    try {
      return await playlistService.removePlaylistItem(playlistId);
    } catch (error) {
      toast.error("Failed to delete playlist item");
      throw error;
    }
  };

  const addCreativeToSubPlaylistMutation = useMutation({
    mutationFn: ({
      subPlaylistId,
      creativeIds,
      startPlayOrder,
    }: {
      subPlaylistId: string;
      creativeIds: string[];
      startPlayOrder?: number;
    }) =>
      playlistService.addCreativeToSubPlaylist(
        subPlaylistId,
        creativeIds,
        startPlayOrder,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      const count = variables.creativeIds.length;
      toast.success(
        count === 1
          ? "Creative added to playlist successfully"
          : `${count} creatives added to playlist successfully`,
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add creatives to playlist");
    },
  });

  const addSubPlaylistsToMainPlaylistMutation = useMutation({
    mutationFn: ({
      mainPlaylistId,
      subPlaylistIds,
      startPlayOrder,
    }: {
      mainPlaylistId: string;
      subPlaylistIds: string[];
      // 1-based slot for the first item; omit to append.
      startPlayOrder?: number;
    }) =>
      playlistService.addSubPlaylistsToMainPlaylist(
        mainPlaylistId,
        subPlaylistIds,
        startPlayOrder,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      const count = variables.subPlaylistIds.length;
      toast.success(
        count === 1
          ? "Sub playlist added to main playlist successfully"
          : `${count} sub playlists added to main playlist successfully`,
      );
    },
    onError: (error: Error) => {
      toast.error(
        error.message || "Failed to add sub playlists to main playlist",
      );
    },
  });

  const updateAssignmentCountMutation = useMutation({
    mutationFn: ({
      assignmentId,
      count,
    }: {
      assignmentId: string;
      count: number;
    }) => playlistService.updateAssignmentCount(assignmentId, count),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      toast.success("Assignment count updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update assignment count");
    },
  });

  const reorderPlaylistItemsMutation = useMutation({
    mutationFn: ({
      playlistId,
      assignmentIds,
    }: {
      playlistId: string;
      assignmentIds: string[];
    }) => playlistService.reorderPlaylistItems(playlistId, assignmentIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playlist-assignments"] });
      queryClient.refetchQueries({
        queryKey: ["playlist-assignments"],
        type: "active",
      });
      toast.success("Playlist items reordered successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reorder playlist items");
    },
  });

  return {
    playlists,
    isPlaylistsLoading,
    refetchPlaylists: refetch,
    createPlaylist: createPlaylistMutation.mutate,
    updatePlaylist: updatePlaylistMutation.mutate,
    deletePlaylists: deletePlaylistMutation.mutate,
    clonePlaylist: clonePlaylistMutation.mutate,
    fetchPlaylistItems,
    deletePlaylistItem,
    addCreativeToSubPlaylist: addCreativeToSubPlaylistMutation.mutateAsync,
    addSubPlaylistsToMainPlaylist:
      addSubPlaylistsToMainPlaylistMutation.mutateAsync,
    updateAssignmentCount: updateAssignmentCountMutation.mutateAsync,
    reorderPlaylistItems: reorderPlaylistItemsMutation.mutateAsync,
  };
};

export const usePlaylistAssignments = (playlistId?: string) => {
  const queryClient = useQueryClient();

  const {
    data: playlistAssignments,
    isLoading: isPlaylistAssignmentsLoading,
    refetch: refetchPlaylistAssignments,
  } = useQuery({
    queryKey: ["playlist-assignments", playlistId],
    queryFn: async () => {
      if (!playlistId) return [];
      return await playlistService.getPlaylistItems(playlistId);
    },
    enabled: Boolean(playlistId),
  });

  const deletePlaylistItemMutation = useMutation({
    mutationFn: playlistService.removePlaylistItem,
    onSuccess: () => {
      toast.success("Playlist item deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["playlist-assignments", playlistId],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete playlist item");
    },
  });

  return {
    playlistAssignments,
    isPlaylistAssignmentsLoading:
      isPlaylistAssignmentsLoading || deletePlaylistItemMutation.isPending,
    refetchPlaylistAssignments,
    deletePlaylistItem: deletePlaylistItemMutation.mutate,
  };
};
